import { useSelector } from 'react-redux';

/**
 * Detail pages read their record from a persisted Redux store, so on load the
 * store still holds the record viewed LAST time. Opened from an email link, the
 * page used to show — and its buttons act on — that other record until the fetch
 * returned, and a slower response for a record the user had already left could
 * overwrite the current one ("click the link 2–3 times to get the right job").
 *
 * Reducer side: wrap the detail thunk's cases.
 *   .addCase(getX.pending,   detailPending())
 *   .addCase(getX.fulfilled, detailSettled((state, action) => handlePayload(state, action)))
 *   .addCase(getX.rejected,  detailSettled())
 *
 * Component side: `useDetailRecord('slice_name', params.id)` returns the record
 * only when it is the one in the URL.
 */

const defaultIdOf = (arg) => arg;

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/** Tracking lives under state.detailRequests[dataKey] = { id, loading }. */
const requestOf = (state, dataKey) => state.detailRequests?.[dataKey];

export const detailPending = ({ idOf = defaultIdOf, dataKey = 'recordData' } = {}) => (state, action) => {
  const id = String(idOf(action.meta.arg));
  state.detailRequests = { ...state.detailRequests, [dataKey]: { id, loading: true } };
  // Another record: drop the old one. Refreshing the same record keeps it on screen.
  if (!sameId(state[dataKey]?.id, id)) state[dataKey] = {};
};

/** Skips responses for a record the user has since left, then runs `reducer`. */
export const detailSettled = (reducer, { idOf = defaultIdOf, dataKey = 'recordData' } = {}) => (state, action) => {
  const request = requestOf(state, dataKey);
  const id = String(idOf(action.meta.arg));
  if (request && request.id !== id) return;

  // Keep why it failed so the page can say so instead of a generic message.
  const error = action.type?.endsWith('/rejected')
    ? (action.payload?.error || action.payload?.message || action.error?.message || 'Failed to load')
    : null;
  if (request) state.detailRequests = { ...state.detailRequests, [dataKey]: { id, loading: false, error } };
  if (reducer) reducer(state, action);
};

/**
 * `record` is null until the store holds the record of `id`; `waiting` tells a
 * spinner (fetch running or not sent yet) from a record that failed to load;
 * `loading` is true while the record of `id` is being (re)fetched; `error` is
 * why its fetch failed.
 */
export const selectDetailRecord = (slice, id, dataKey = 'recordData') => {
  const data = slice?.[dataKey];
  const request = slice?.detailRequests?.[dataKey];
  const current = sameId(data?.id, id);
  const requestedHere = request?.id === String(id);
  return {
    record: current ? data : null,
    waiting: !current && (!requestedHere || request.loading),
    loading: requestedHere && request.loading,
    error: (!current && requestedHere && request.error) || null,
  };
};

export const useDetailRecord = (sliceName, id, dataKey = 'recordData') => {
  const data = useSelector((state) => state[sliceName]?.[dataKey]);
  const request = useSelector((state) => state[sliceName]?.detailRequests?.[dataKey]);
  return selectDetailRecord({ [dataKey]: data, detailRequests: { [dataKey]: request } }, id, dataKey);
};

/** Stable stand-in while `record` is null, for pages whose hooks read fields off it. */
export const EMPTY_RECORD = Object.freeze({});
